import React, {memo, useRef, useState} from 'react';
import {Controller} from 'react-hook-form';
import {TextField} from '@ui/molecules/field/textfield';
import {SelectField} from '@ui/molecules/select/selectfield';
import {ItemLoader} from '@ui/organisms/item-loader';
import {useStoreContext} from '../../../../../../store/store-context';
import {authStore} from '../../../../../../store/stores/auth-store';
import {timezones} from '../../../../../../constants';
import {getStrBtwCharacters} from '../../../../../../utils/strings';
import {BaseURL} from '../../../../../../configs/request';
import {activateNotification} from '../../../../../../ui/molecules/notification/activate-notification';
import {useAdminWorkspaceHook} from '../admin-workspace-hooks';
import {
  GeneralPage,
  GeneralPageIntro,
  GeneralStack,
  SettingsCard,
  WorkspaceAvatar,
  WorkspaceAvatarAction,
  WorkspaceAvatarFallback,
  WorkspaceCardBody,
  WorkspaceFields,
  WorkspaceField,
  WorkspaceActions,
  SaveChangesButton,
  SectionIntro,
  HiddenFileInput,
} from './admin-workspace-general.styles';

export const AdminSettings = memo(() => {
  const {
    isSubmitting,
    handleSubmit,
    submitForm,
    register,
    errors,
    control,
    image,
    handleChange,
    isDisabled,
    handleSelectChange,
    base64Upload,
    users,
    isLoading,
  } = useAdminWorkspaceHook();
  const {
    authStore: {setInitResponseAsAuth, setAuthResponse, auth},
  } = useStoreContext();
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const uploadAvatar = async () => {
    if (!base64Upload) {
      hiddenFileInput.current?.click();
      return;
    }

    setUploadingAvatar(true);
    try {
      const response = await fetch(
        `${BaseURL}/workspaces/${authStore.auth?.user.workspace.id}/upload-avatar`,
        {
          method: 'POST',
          headers: {
            'content-Type': 'application/json',
            Authorization: `Bearer ${authStore.auth?.tokens.access.token}`,
          },
          body: JSON.stringify({avatar: base64Upload}),
        },
      );
      const workspace = await response.json();

      if (!response.ok)
        throw new Error(workspace?.message || response.statusText);

      if (localStorage.auth) {
        const storedAuthObject = JSON.parse(localStorage.auth);
        storedAuthObject.user.workspace = workspace;
        localStorage.setItem('auth', JSON.stringify(storedAuthObject));
        setAuthResponse(storedAuthObject);
      }
      setInitResponseAsAuth();
      activateNotification({
        title: 'Success',
        content: 'Workspace avatar uploaded successfully',
        kind: 'success',
      });
    } catch (error: any) {
      activateNotification({
        title: 'Error',
        content: error?.message || 'Workspace avatar could not be uploaded',
        kind: 'error',
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (!isLoading) return <ItemLoader />;

  return (
    <GeneralPage>
      <GeneralPageIntro>
        <h1>Workspace Settings</h1>
        <p>Manage your workspace profile and timezone.</p>
      </GeneralPageIntro>

      <GeneralStack>
        <SettingsCard>
          <WorkspaceCardBody>
            <WorkspaceAvatar>
              {image.preview || users.avatar ? (
                <img
                  src={image.preview || users.avatar}
                  alt={`${users.Name} workspace icon`}
                />
              ) : (
                <WorkspaceAvatarFallback>
                  {users.Name?.charAt(0)?.toUpperCase() || 'B'}
                </WorkspaceAvatarFallback>
              )}
              <WorkspaceAvatarAction
                type="button"
                kind="secondary"
                isLoading={uploadingAvatar}
                onClick={uploadAvatar}>
                {image.preview ? 'Save icon' : 'Change icon'}
              </WorkspaceAvatarAction>
              <HiddenFileInput
                type="file"
                ref={hiddenFileInput}
                accept="image/*"
                onChange={handleChange}
              />
            </WorkspaceAvatar>

            <SectionIntro>
              <h2>Workspace Profile</h2>
              <p>Basic information about your organization.</p>
            </SectionIntro>

            <form method="patch" onSubmit={handleSubmit(submitForm)}>
              <WorkspaceFields>
                <WorkspaceField>
                  <TextField
                    label="Workspace Name"
                    defaultValue={users.Name}
                    placeholder="Workspace Name"
                    autoComplete="organization"
                    type="text"
                    ref={register}
                    helper={errors.name?.message || ''}
                    state={errors.name ? 'error' : 'default'}
                    name="name"
                  />
                </WorkspaceField>
                <WorkspaceField>
                  <TextField
                    label="Contact Email"
                    defaultValue={users.email}
                    placeholder="Contact Email"
                    type="email"
                    ref={register}
                    helper={errors.contactEmail?.message || ''}
                    state={errors.contactEmail ? 'error' : 'default'}
                    name="contactEmail"
                  />
                </WorkspaceField>
                <WorkspaceField>
                  <Controller
                    control={control}
                    name="timezone"
                    defaultValue={users.Timezone}
                    render={({onBlur, value}) => (
                      <SelectField
                        onBlur={onBlur}
                        name="timezone"
                        selected="first"
                        placeholder="Select a time-zone"
                        value={value}
                        label="Time Zone"
                        options={timezones.map(
                          (timezone: {label: string; value: string}) => ({
                            label: `${timezone.label} (${getStrBtwCharacters(
                              timezone.value,
                              '(',
                              ')',
                            )})`,
                            value: timezone.value,
                          }),
                        )}
                        helper={errors.timezone?.message || ''}
                        state={errors.timezone ? 'error' : 'default'}
                        onChange={(data: {value: string}) =>
                          data && handleSelectChange(data.value)
                        }
                      />
                    )}
                  />
                </WorkspaceField>
              </WorkspaceFields>
              <WorkspaceActions>
                <SaveChangesButton
                  type="submit"
                  disabled={isDisabled}
                  data-form-action={true}
                  isLoading={isSubmitting}
                  aria-busy={isSubmitting}>
                  Save Changes
                </SaveChangesButton>
              </WorkspaceActions>
            </form>
          </WorkspaceCardBody>
        </SettingsCard>

      </GeneralStack>
    </GeneralPage>
  );
});

AdminSettings.displayName = 'AdminSettings';
